import {
  createWorkflow,
  getWorkspaceSettings,
  listWorkspaceAgents,
  updateWorkflow,
  listPortfolioFiles,
  runWorkflow,
  uploadFile,
  type CoworkWorkflow,
} from "@/cowork/api"
import { buildCoworkReportTemplatePrompt, coworkReportOutputLanguageInstructionValue } from "@/cowork/report-prompt"
import { coworkAccountingReportTemplates } from "@/cowork/report-templates"
import { workspaceHref } from "@/cowork/workspace-route"
import { useLanguage } from "@/context/language"
import { BackLabel } from "@/components/back-label"
import { createMemo, createResource, For, Show, onCleanup } from "solid-js"
import { createStore } from "solid-js/store"
import {
  createWorkflowRunAttempt,
  workflowUploadsReady,
  workflowWizardValues,
  workflowSourceChoices,
  workflowSourceFileAvailable,
  workflowReportAgents,
  type WorkflowUpload,
} from "./cowork-workflow-reporting"

export type WorkflowWizardScope = { companyID: string; projectID: string; periodID?: string | null; label: string }

export function CoworkWorkflowWizard(props: {
  scope: WorkflowWizardScope
  workflow?: CoworkWorkflow
  onCancel: () => void
  onSaved: (workflow: CoworkWorkflow) => void
  onStarted: (workflowID: string, runID: string, workflow: CoworkWorkflow) => void
}) {
  const language = useLanguage()
  // The header may change while this form is open; it must never retarget a write.
  const existing = props.workflow
  const initial = workflowWizardValues(existing)
  const scope = Object.freeze({
    ...props.scope,
    companyID: existing?.company_id ?? props.scope.companyID,
    projectID: existing?.project_id ?? props.scope.projectID,
    periodID: initial.reporting ? initial.reporting.period_id : props.scope.periodID,
  })
  const [state, setState] = createStore({
    step: 0,
    name: initial.name,
    templateID: initial.templateID,
    agentID: existing?.default_agent_id ?? "",
    customPrompt: initial.reporting?.template_id === "custom-report" ? initial.reporting.visible_prompt : "",
    selectedIDs: initial.fileIDs,
    files: [] as WorkflowUpload[],
    busy: false,
    error: "" as "" | "saveFailed" | "startFailed" | "trialReportLimit",
    saved: undefined as CoworkWorkflow | undefined,
    mode: "save+generate" as "save" | "save+generate",
  })
  const [sources, sourceActions] = createResource(() =>
    listPortfolioFiles({ entityID: scope.companyID, projectID: scope.projectID }),
  )
  const [agents] = createResource(listWorkspaceAgents)
  const [workspaceSettings] = createResource(getWorkspaceSettings)
  const choices = createMemo(() =>
    workflowSourceChoices(sources.error ? [] : (sources() ?? []), state.selectedIDs, scope),
  )
  let disposed = false
  onCleanup(() => {
    disposed = true
  })
  const attempt = createWorkflowRunAttempt((workflow: CoworkWorkflow, requestID) =>
    runWorkflow(scope.projectID, workflow.id, scope.periodID ?? undefined, {
      requestID,
      companyID: scope.companyID,
      applicationLocale: language.locale(),
    }),
  )
  const nameReady = () => !!state.name.trim() && state.name.trim().length <= 255
  const filesReady = () =>
    !sources.loading &&
    !sources.error &&
    choices().every((file) => !file.selected || file.available) &&
    (state.files.length ? workflowUploadsReady(state.files) : state.selectedIDs.length > 0)
  const template = () => coworkAccountingReportTemplates.find((item) => item.id === state.templateID)
  const requiredSkills = () => state.templateID === "custom-report"
    ? ["custom-report-workflow"] : template()?.requiredSkills ?? []
  const eligibleAgents = createMemo(() => workflowReportAgents(agents() ?? [], scope, requiredSkills()))
  const agentID = () => state.agentID || (!existing ? workspaceSettings()?.default_agent_id ?? "" : "")
  const templateReady = () => eligibleAgents().some((agent) => agent.id === agentID()) && (
    state.templateID === "custom-report"
      ? !!state.customPrompt.trim()
      : !!template() && (state.templateID !== "account-card" || !!scope.periodID)
  )

  const upload = async (key: string) => {
    const item = state.files.find((file) => file.key === key)
    if (!item || state.busy || state.saved) return
    setState("files", (file) => file.key === key, { status: "uploading", fileID: undefined })
    try {
      const result = await uploadFile(
        scope.projectID,
        item.file,
        scope.periodID ?? null,
        undefined,
        scope.companyID,
      )
      if (disposed) return
      if (!workflowSourceFileAvailable(result, scope)) throw new Error()
      setState("files", (file) => file.key === key, { status: "success", fileID: result.id })
    } catch {
      if (!disposed) setState("files", (file) => file.key === key, { status: "failure", fileID: undefined })
    }
  }

  const save = async (mode: "save" | "save+generate") => {
    const selected = template()
    const customReport = state.templateID === "custom-report"
    if (
      state.busy ||
      !nameReady() ||
      !filesReady() ||
      (!selected && !customReport) ||
      !templateReady() ||
      (mode === "save+generate" && existing && existing.status !== "active")
    )
      return
    setState({ busy: true, error: "", mode })
    try {
      let workflow = state.saved
      if (!workflow) {
        const prompt = selected
          ? buildCoworkReportTemplatePrompt(selected, language.t, initial.reporting?.account_card ?? undefined)
          : { visiblePrompt: state.customPrompt.trim() }
        const input = {
          name: state.name.trim(),
          default_agent_id: agentID(),
          reporting: {
            ...(initial.reporting?.template_id === state.templateID && !customReport
               ? initial.reporting
               : {
                   template_id: customReport ? "custom-report" : selected!.id,
                   template_version: customReport ? 1 : selected!.version,
                   report_type: customReport ? "custom_report" : selected!.reportType,
                   visible_prompt: prompt.visiblePrompt,
                   report_language: coworkReportOutputLanguageInstructionValue(language.locale()),
                 }),
            file_ids: [...new Set([...state.selectedIDs, ...state.files.map((file) => file.fileID!)])],
            period_id: scope.periodID ?? null,
            ...(selected?.id === "account-card" ? {
                account_card: initial.reporting?.account_card ?? null,
                visible_prompt: prompt.visiblePrompt,
            } : customReport ? { visible_prompt: prompt.visiblePrompt } : {}),
          },
        }
        workflow = existing
          ? await updateWorkflow(scope.projectID, existing.id, input, scope.companyID)
          : await createWorkflow(
              scope.projectID,
               { ...input, approval_required: false },
              scope.companyID,
            )
        if (disposed) return
        setState("saved", workflow)
      }
      if (mode === "save") {
        props.onSaved(workflow)
        return
      }
      const run = await attempt.start(workflow)
      if (!disposed && run) props.onStarted(workflow.id, run.id, workflow)
    } catch (error) {
      const trial = error instanceof Error && error.message === "trial_report_limit"
      if (!disposed) setState("error", trial ? "trialReportLimit" : state.saved ? "startFailed" : "saveFailed")
    } finally {
      if (!disposed) setState("busy", false)
    }
  }

  const button = "rounded-lg border border-v2-border-border-base px-4 py-2 text-[12px] disabled:opacity-50"
  return (
    <section
      class="mt-6 rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-5 sm:p-7"
      aria-label={language.t(
        existing ? "cowork.enterprise.workflows.reporting.edit" : "cowork.enterprise.workflows.reporting.title",
      )}
    >
      <h2 class="text-[18px] font-semibold">
        {language.t(
          existing ? "cowork.enterprise.workflows.reporting.edit" : "cowork.enterprise.workflows.reporting.title",
        )}
      </h2>
      <p class="mt-1 text-[12px] text-v2-text-text-muted">{scope.label}</p>
      <p class="mt-1 break-all text-[12px] text-v2-text-text-muted">
        {language.t("cowork.enterprise.workflows.reporting.fixedPeriod", {
          period: scope.periodID || language.t("cowork.enterprise.workflows.reporting.unassignedPeriod"),
        })}
      </p>
      <ol class="my-6 grid grid-cols-3 gap-2 text-[12px]">
        <For each={["name", "files", "template"] as const}>
          {(step, index) => (
            <li
              class="border-b-2 border-v2-border-border-base pb-3"
              classList={{ "border-v2-border-border-strong font-semibold": state.step === index() }}
              aria-current={state.step === index() ? "step" : undefined}
            >
              {index() + 1}.{" "}
              {language.t(
                step === "files"
                  ? "cowork.enterprise.workflows.reporting.inputSelection"
                  : `cowork.enterprise.workflows.reporting.step.${step}`,
              )}
            </li>
          )}
        </For>
      </ol>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (state.busy) return
          if (state.step === 0 && nameReady()) setState("step", 1)
          else if (state.step === 1 && filesReady()) setState("step", 2)
          else if (state.step === 2) void save("save+generate")
        }}
      >
        <fieldset disabled={state.busy || !!state.saved}>
          <Show when={state.step === 0}>
            <label class="block max-w-xl text-[12px] font-medium">
              {language.t("cowork.enterprise.workflows.name")}
              <input
                required
                maxLength={255}
                class="mt-2 h-11 w-full rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3"
                value={state.name}
                onInput={(event) => setState("name", event.currentTarget.value)}
                placeholder={language.t("cowork.enterprise.workflows.namePlaceholder")}
              />
            </label>
          </Show>
          <Show when={state.step === 1}>
            <fieldset class="mb-5">
              <legend class="mb-3 text-[14px] font-semibold">
                {language.t("cowork.enterprise.workflows.reporting.existingFiles")}
              </legend>
              <Show when={sources.loading}>
                <p role="status" class="text-[12px]">
                  {language.t("common.loading")}
                </p>
              </Show>
              <Show when={sources.error}>
                <p role="alert" class="text-[12px]">
                  {language.t("cowork.enterprise.workflows.reporting.filesLoadFailed")}
                </p>
                <button type="button" class={button} onClick={() => void sourceActions.refetch()}>
                  {language.t("cowork.enterprise.workflows.reporting.retry")}
                </button>
              </Show>
              <Show when={!sources.loading && !sources.error && !choices().length}>
                <p class="text-[12px] text-v2-text-text-muted">
                  {language.t("cowork.enterprise.workflows.reporting.noSourceFiles")}
                </p>
              </Show>
              <ul class="space-y-2">
                <For each={choices()}>
                  {(file) => (
                    <li class="flex flex-wrap items-center gap-3 rounded-lg bg-v2-background-bg-layer-01 p-3 text-[12px]">
                      <label class="flex min-w-0 flex-1 items-center gap-3">
                        <input
                          type="checkbox"
                          checked={file.selected}
                          disabled={!file.available}
                          onChange={(event) =>
                            setState("selectedIDs", (ids) =>
                              event.currentTarget.checked ? [...ids, file.id] : ids.filter((id) => id !== file.id),
                            )
                          }
                        />
                        <span class="break-all">{file.name}</span>
                      </label>
                      <Show when={!file.available}>
                        <span class="text-v2-state-fg-danger">
                          {language.t("cowork.enterprise.workflows.reporting.missingSource")}
                        </span>
                        <button
                          type="button"
                          class={button}
                          aria-label={language.t("cowork.enterprise.workflows.reporting.removeFile", {
                            name: file.name,
                          })}
                          onClick={() => setState("selectedIDs", (ids) => ids.filter((id) => id !== file.id))}
                        >
                          {language.t("cowork.enterprise.workflows.reporting.remove")}
                        </button>
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </fieldset>
            <label class="block rounded-lg border border-dashed border-v2-border-border-base p-5 text-[13px]">
              <span class="block font-medium">{language.t("cowork.enterprise.workflows.reporting.upload")}</span>
              <input
                type="file"
                multiple
                class="mt-3 block max-w-full text-[12px]"
                onChange={(event) => {
                  const files = Array.from(event.currentTarget.files ?? []).map(
                    (file): WorkflowUpload => ({ key: crypto.randomUUID(), file, status: "uploading" }),
                  )
                  setState("files", (current) => [...current, ...files])
                  event.currentTarget.value = ""
                  for (const file of files) void upload(file.key)
                }}
              />
            </label>
            <p class="mt-2 text-[12px] text-v2-text-text-muted">
              {language.t("cowork.enterprise.workflows.reporting.inputHint")}
            </p>
            <ul class="mt-4 space-y-2" aria-live="polite">
              <For each={state.files}>
                {(file) => (
                  <li class="flex flex-wrap items-center gap-3 rounded-lg bg-v2-background-bg-layer-01 p-3 text-[12px]">
                    <span class="min-w-0 flex-1 break-all">{file.file.name}</span>
                    <span>{language.t(`cowork.enterprise.workflows.reporting.upload.${file.status}`)}</span>
                    <Show when={file.status === "failure"}>
                      <button type="button" class={button} onClick={() => void upload(file.key)}>
                        {language.t("cowork.enterprise.workflows.reporting.retry")}
                      </button>
                    </Show>
                    <button
                      type="button"
                      class={button}
                      aria-label={language.t("cowork.enterprise.workflows.reporting.removeFile", {
                        name: file.file.name,
                      })}
                      onClick={() => setState("files", (files) => files.filter((item) => item.key !== file.key))}
                    >
                      {language.t("cowork.enterprise.workflows.reporting.remove")}
                    </button>
                  </li>
                )}
              </For>
            </ul>
          </Show>
          <Show when={state.step === 2}>
            <fieldset>
              <legend class="mb-3 text-[14px] font-semibold">
                {language.t("cowork.enterprise.workflows.reporting.chooseTemplate")}
              </legend>
              <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <label
                  class="cursor-pointer rounded-lg border border-v2-border-border-base p-4"
                  classList={{ "border-v2-border-border-strong bg-v2-background-bg-layer-01": state.templateID === "custom-report" }}
                >
                  <span class="flex items-start gap-2">
                    <input
                      class="mt-1"
                      type="radio"
                      name="workflow-report-template"
                      value="custom-report"
                      aria-label={language.t("cowork.agents.template.customReport")}
                      checked={state.templateID === "custom-report"}
                      onChange={() => setState("templateID", "custom-report")}
                    />
                    <span class="text-[13px] font-semibold">{language.t("cowork.agents.template.customReport")}</span>
                  </span>
                  <span class="mt-2 block text-[12px] text-v2-text-text-muted">
                    {language.t("cowork.agents.template.customReport.description")}
                  </span>
                </label>
                <For each={coworkAccountingReportTemplates}>
                  {(item) => (
                    <label
                      class="cursor-pointer rounded-lg border border-v2-border-border-base p-4"
                      classList={{
                        "border-v2-border-border-strong bg-v2-background-bg-layer-01": state.templateID === item.id,
                      }}
                    >
                      <span class="flex items-start gap-2">
                        <input
                          class="mt-1"
                          type="radio"
                          name="workflow-report-template"
                          value={item.id}
                          aria-label={language.t(item.titleKey)}
                          aria-describedby={`workflow-template-${item.id}-description`}
                          checked={state.templateID === item.id}
                          onChange={() => setState("templateID", item.id)}
                        />
                        <span class="text-[13px] font-semibold">{language.t(item.titleKey)}</span>
                      </span>
                      <span
                        id={`workflow-template-${item.id}-description`}
                        class="mt-2 block text-[12px] text-v2-text-text-muted"
                      >
                        {language.t(item.descriptionKey)}
                      </span>
                    </label>
                  )}
                </For>
              </div>
            </fieldset>
            <label class="mt-4 block max-w-md text-[12px] font-medium">
              {language.t("cowork.enterprise.workflows.agent")}
              <select
                class="mt-2 block w-full rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3"
                value={agentID()}
                onChange={(event) => setState("agentID", event.currentTarget.value)}
              >
                <option value="">{language.t("cowork.enterprise.workflows.agent")}</option>
                <For each={eligibleAgents()}>{(agent) => <option value={agent.id}>{agent.name}</option>}</For>
              </select>
            </label>
            <Show when={!agents.loading && eligibleAgents().length === 0}>
              <a class="mt-2 inline-block text-[12px] underline" href={workspaceHref("/agents")}>
                {language.t("cowork.enterprise.nav.myAgents")}
              </a>
            </Show>
            <Show when={state.templateID === "custom-report"}>
              <label class="mt-4 block max-w-2xl text-[12px] font-medium">
                {language.t("cowork.enterprise.workflows.purpose")}
                <textarea
                  class="mt-2 block w-full rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3"
                  rows={4}
                  value={state.customPrompt}
                  onInput={(event) => setState("customPrompt", event.currentTarget.value)}
                  required
                />
              </label>
            </Show>
            <Show when={state.templateID === "account-card"}>
              <Show when={!scope.periodID}>
                <p class="mt-4 text-[12px] text-v2-text-text-muted">
                  {language.t("cowork.agent.settings.template.accountCard.selectPeriod")}
                </p>
              </Show>
            </Show>
            <p class="mt-4 text-[12px] text-v2-text-text-muted">
              {language.t("cowork.enterprise.workflows.reporting.saveHint")}
            </p>
          </Show>
        </fieldset>
        <Show when={state.error}>
          <p role="alert" class="mt-4 rounded-lg bg-v2-state-bg-danger p-3 text-[12px] text-v2-state-fg-danger">
            {language.t(`cowork.enterprise.workflows.reporting.${state.error || "saveFailed"}`)}
          </p>
        </Show>
        <div class="mt-6 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            class={button}
            disabled={state.busy}
            onClick={() => (state.saved ? props.onSaved(state.saved) : props.onCancel())}
          >
            {language.t("common.cancel")}
          </button>
          <Show when={state.step > 0}>
            <button
              type="button"
              class={button}
              disabled={state.busy || !!state.saved}
              onClick={() => setState("step", (step) => step - 1)}
            >
              <BackLabel>{language.t("cowork.enterprise.workflows.reporting.back")}</BackLabel>
            </button>
          </Show>
          <Show when={state.step === 2 && !state.saved}>
            <button
              type="button"
              class={button}
              disabled={state.busy || !filesReady() || !templateReady() || !nameReady()}
              onClick={() => void save("save")}
            >
              {language.t("cowork.enterprise.workflows.reporting.save")}
            </button>
          </Show>
          <button
            type="submit"
            class="rounded-lg bg-v2-background-bg-contrast px-5 py-2 text-[12px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
            disabled={
              state.busy ||
              (state.step === 2 && !!existing && existing.status !== "active") ||
              (state.step === 0 ? !nameReady() : state.step === 1 ? !filesReady() : !filesReady() || !templateReady())
            }
          >
            {language.t(
              state.busy
                ? state.mode === "save"
                  ? "cowork.enterprise.workflows.reporting.saveBusy"
                  : "cowork.enterprise.workflows.reporting.saving"
                : state.saved && state.step === 2
                  ? "cowork.enterprise.workflows.reporting.retryStart"
                  : state.step === 2
                    ? "cowork.enterprise.workflows.reporting.generate"
                    : "cowork.enterprise.workflows.reporting.next",
            )}
          </button>
        </div>
      </form>
    </section>
  )
}
